import { useContext, useEffect } from "react";
import { FormGroup, Form, InputGroup, Spinner, Card, Alert } from "reactstrap";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { isEmpty } from "lodash";

import SiteTitle from "../../../helpers/SiteTitle/SiteTitle";
import ErrorMessage from "../../../helpers/ErrorMessage/ErrorMessage";
import Button from "./../../../components/Button/Button";
import Icon from "../../../components/Icon/Icon";
import PageHeader from "../../../components/PageHeader/PageHeader";
import AvatarCropper from "../../../components/AvatarCropper/AvatarCropper";
import OLMap from "../../../helpers/OLMap/OLMap";
import { Col, Row } from "./../../../components/Grid/Grid";
import { infoShopContext } from "../../../context/ShopContext/InfoShopContext/InfoShopContext";
import { createShopInfoValidation } from "../../../utils/validation/validation";
import {
  categoryItems,
  convertCategoryToText,
  uploadTypeItems,
} from "../../../utils/enum";
import { Block } from "../../../components/Block/Block";
import { areYouSureMessage } from "../../../utils/toastMessage/toastMessage";

const InfoShop = () => {
  const loader = useSelector((state) => state.loader);

  const {
    info,
    handleGetInfo,
    handleEditInfo,
    handleChangeInfoActive,
    handleUploadFile,
  } = useContext(infoShopContext);

  // Validation
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(createShopInfoValidation),
  });

  useEffect(() => {
    if (!isEmpty(info)) {
      setValue("avatar", info?.avatar);
      setValue("pictures", info?.pictures);
      setValue("features", info?.features);
      setValue("title", info?.title);
      setValue("address", info?.address);
      setValue("type", info?.type);
      setValue("description", info?.description);
      setValue("geo", info?.geo);
    }
  }, [info]);

  useEffect(() => {
    handleGetInfo();
  }, []);

  const handleAddItem = (type, value) => {
    switch (type) {
      case "pictures":
        setValue(type, [...(watch(type) ?? []), value]);
        break;
      case "features":
        setValue(type, [...(watch(type) ?? []), ""]);
        break;

      default:
        break;
    }
  };
  const handleRemoveItem = (type, index) =>
    setValue(
      type,
      watch(type)?.filter((_, _i) => _i !== index)
    );

  return (
    <>
      <SiteTitle title="اطلاعات فروشگاه" />

      <PageHeader title="اطلاعات فروشگاه" description="" />

      <Card className="card-inner card-inner">
        <Alert
          className="text-center"
          color={info.isActive ? "success" : "danger"}
        >
          صفحه اطلاعات فروشگاه{" "}
          <span className="fw-bold">{info.isActive ? "فعال" : "غیرفعال"}</span>{" "}
          است
        </Alert>
        <Block>
          <div className="data-head mb-3">
            <h6 className="overline-title">اطلاعات فروشگاه</h6>
          </div>
          <Form className="is-alter" onSubmit={handleSubmit(handleEditInfo)}>
            <div className="form-control-wrap">
              <input type="hidden" className="form-control" />
              <ErrorMessage message={errors.avatar?.message} />
            </div>
            <AvatarCropper
              onRemove={() => setValue("avatar", "")}
              value={watch("avatar")}
              onChange={(file) =>
                handleUploadFile(file, uploadTypeItems[1], (e) =>
                  setValue("avatar", e)
                )
              }
            />

            <Row className="gy-4">
              <Col md="6">
                <FormGroup>
                  <label className="form-label" htmlFor="title">
                    عنوان
                  </label>
                  <div className="form-control-wrap">
                    <input
                      type="text"
                      placeholder="عنوان فروشگاه را وارد نمایید"
                      className="form-control-lg form-control"
                      {...register("title")}
                    />
                    <ErrorMessage message={errors.title?.message} />
                  </div>
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <label className="form-label" htmlFor="type">
                    نوع فروشگاه
                  </label>
                  <div className="form-control-wrap">
                    <select
                      disabled={!isEmpty(info)}
                      className="form-control-lg form-control"
                      {...register("type")}
                    >
                      <option value="" selected hidden>
                        نوع فروشگاه را انتخاب نمایید
                      </option>
                      {categoryItems.map((item) => (
                        <option
                          key={item}
                          value={item}
                          selected={item === watch("type")}
                        >
                          {convertCategoryToText(item)}
                        </option>
                      ))}
                    </select>
                    <ErrorMessage message={errors.type?.message} />
                  </div>
                </FormGroup>
              </Col>
              <Col size="12">
                <FormGroup>
                  <label className="form-label" htmlFor="address">
                    آدرس
                  </label>
                  <div className="form-control-wrap">
                    <input
                      type="text"
                      placeholder="آدرس فروشگاه را وارد نمایید"
                      className="form-control-lg form-control"
                      {...register("address")}
                    />
                    <ErrorMessage message={errors.address?.message} />
                  </div>
                </FormGroup>
              </Col>
              <Col size="12">
                <FormGroup>
                  <label className="form-label" htmlFor="address">
                    مختصات جغرافیایی
                  </label>
                  <div className="form-control-wrap">
                    <input type="hidden" className="form-control" />
                    <ErrorMessage message={errors.geo?.message} />
                    <ErrorMessage message={errors.geo?.latitude?.message} />
                    <ErrorMessage message={errors.geo?.longitude?.message} />
                    <OLMap
                      latitude={watch("geo.latitude")}
                      longitude={watch("geo.longitude")}
                      style={{ width: "auto", height: "400px" }}
                      onChange={(e) => {
                        setValue("geo.latitude", e.originalLatitude);
                        setValue("geo.longitude", e.originalLongitude);
                      }}
                    />
                  </div>
                </FormGroup>
              </Col>
              <Col size="12">
                <FormGroup>
                  <label className="form-label" htmlFor="description">
                    درباره
                  </label>
                  <div className="form-control-wrap">
                    <textarea
                      placeholder="درباره فروشگاه را وارد نمایید"
                      className="form-control-lg form-control"
                      {...register("description")}
                      rows={7}
                    />
                    <ErrorMessage message={errors.description?.message} />
                  </div>
                </FormGroup>
              </Col>

              <Col size="12">
                <div className="data-head my-3">
                  <div className="form-control-wrap">
                    <input type="hidden" className="form-control" />
                    <ErrorMessage message={errors.pictures?.message} />
                  </div>
                  <h6 className="overline-title">تصاویر</h6>
                </div>
                <label htmlFor="uploadFile" className="btn btn-outline-primary">
                  <input
                    disabled={loader}
                    type="file"
                    multiple
                    accept=".jpg,.jpeg,.png;capture=camera"
                    id="uploadFile"
                    className="d-none"
                    onChange={async (e) => {
                      for (let i = 0; i < e.target.files?.length; i++) {
                        const file = e.target.files[i];
                        await handleUploadFile(file, uploadTypeItems[0], (e) =>
                          handleAddItem("pictures", e)
                        );
                      }
                      document.getElementById(`uploadFile`).value = "";
                    }}
                  />
                  {loader ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      <Icon name="plus" className="mr-3" /> افزودن
                    </>
                  )}
                </label>
                {isEmpty(watch("pictures")) ? (
                  <div className="text-center alert alert-info">
                    <span className="text-silent">
                      تصویری برای نمایش یافت نشد
                    </span>
                  </div>
                ) : (
                  <Row className="gy-4">
                    {watch("pictures")?.map((_, index) => (
                      <Col size="12" md="6" lg="4" key={index}>
                        <InputGroup>
                          <input
                            type="text"
                            placeholder="تصویر فروشگاه را وارد نمایید"
                            className="form-control-lg form-control"
                            {...register(`pictures[${index}]`)}
                            disabled
                            readOnly
                          />
                          <Button
                            type="button"
                            className="btn btn-icon btn-trigger text-danger"
                            onClick={() => handleRemoveItem("pictures", index)}
                          >
                            <Icon name="trash" />
                          </Button>
                          <ErrorMessage
                            message={
                              errors.pictures && errors.pictures[index]?.message
                            }
                          />
                        </InputGroup>
                      </Col>
                    ))}
                  </Row>
                )}
              </Col>
              <Col size="12">
                <div className="data-head my-3">
                  <div className="form-control-wrap">
                    <input type="hidden" className="form-control" />
                    <ErrorMessage message={errors.features?.message} />
                  </div>
                  <h6 className="overline-title">ویژگی ها</h6>
                </div>
                <Button
                  outline
                  type="button"
                  onClick={() => handleAddItem("features")}
                  color="primary"
                  className="mb-2"
                >
                  <Icon name="plus" className="mr-3" /> افزودن
                </Button>
                {isEmpty(watch("features")) ? (
                  <div className="text-center alert alert-info">
                    <span className="text-silent">
                      ویژگی ای برای نمایش یافت نشد
                    </span>
                  </div>
                ) : (
                  <Row className="gy-4">
                    {watch("features")?.map((_, index) => (
                      <Col size="12" md="6" lg="4" key={index}>
                        <InputGroup>
                          <input
                            type="text"
                            placeholder="ویژگی فروشگاه را وارد نمایید"
                            className="form-control-lg form-control"
                            {...register(`features[${index}]`)}
                          />
                          <Button
                            type="button"
                            className="btn btn-icon btn-trigger text-danger"
                            onClick={() => handleRemoveItem("features", index)}
                          >
                            <Icon name="trash" />
                          </Button>
                          <ErrorMessage
                            message={
                              errors.features && errors.features[index]?.message
                            }
                          />
                        </InputGroup>
                      </Col>
                    ))}
                  </Row>
                )}
              </Col>
              <Col size="12">
                <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                  <li>
                    <Button
                      color="primary"
                      size="lg"
                      type="submit"
                      loading={loader}
                    >
                      ویرایش فروشگاه
                    </Button>
                  </li>
                  {!isEmpty(info) && (
                    <li>
                      <Button
                        color={info.isActive ? "danger" : "success"}
                        outline
                        size="lg"
                        type="button"
                        loading={loader}
                        onClick={() =>
                          areYouSureMessage(
                            handleChangeInfoActive,
                            `آیا از ${
                              info.isActive ? "غیرفعالسازی" : "فعالسازی"
                            } فروشگاه اطمینان دارید؟`
                          )
                        }
                      >
                        {info.isActive ? "غیرفعالسازی" : "فعالسازی"} فروشگاه
                      </Button>
                    </li>
                  )}
                </ul>
              </Col>
            </Row>
          </Form>
        </Block>
      </Card>
    </>
  );
};

export default InfoShop;
